import { AlertTriangle, ArrowRight, Coffee, ExternalLink } from 'lucide-react';
import Icon from '@components/ui/Icon';

const KOFI_URL = 'https://ko-fi.com/studioshell';
const HELP_URL = 'https://get.webgl.org/';

/**
 * WebGLGate — shown when the browser cannot create a WebGL context.
 * Catches headless browsers, very old GPUs, disabled hardware
 * acceleration, and corporate-restricted environments before Three.js
 * tries to mount and crashes.
 */
export default function WebGLGate() {
  return (
    <div
      role="dialog"
      aria-label="WebGL required"
      style={{
        minHeight: '100vh',
        width: '100%',
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-2xl)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <main
        style={{
          maxWidth: 460,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-lg)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 'var(--radius-md)',
            background: 'var(--accent-green)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            fontSize: 28,
            color: '#fff',
            lineHeight: 1,
            boxShadow: '0 0 32px rgba(112, 148, 36, 0.35)',
          }}
        >
          S
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            color: 'var(--accent-amber)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '1px',
            textTransform: 'uppercase',
          }}
        >
          <Icon icon={AlertTriangle} size={12} color="var(--accent-amber)" />
          WebGL required
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 26,
            letterSpacing: '-0.5px',
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          Your browser can&apos;t render the&nbsp;3D&nbsp;viewport
        </h1>

        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--text-muted)',
          }}
        >
          StudioShell needs WebGL to draw the room and run the wave simulation.
          This usually means hardware acceleration is disabled, your GPU driver
          is out of date, or you&apos;re inside a restricted browser environment.
        </p>

        <div
          style={{
            width: '100%',
            padding: '14px 16px',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border-dim)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            textAlign: 'left',
          }}
        >
          <Row label="What to try" value="Enable hardware acceleration in browser settings" />
          <Row label="Or" value="Try Chrome / Firefox / Edge on a desktop with a GPU" />
          <Row label="Test" value="visit get.webgl.org" />
        </div>

        <a
          href={HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            background: 'var(--accent-green)',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 14,
            transition: 'background 0.15s',
          }}
        >
          Test WebGL support
          <Icon icon={ArrowRight} size={14} />
        </a>

        <div
          style={{
            display: 'flex',
            gap: 14,
            alignItems: 'center',
            fontSize: 11,
            color: 'var(--text-hint)',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
          }}
        >
          <a
            href={KOFI_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon icon={Coffee} size={12} color="var(--text-hint)" />
            Donate
          </a>
          <span aria-hidden style={{ width: 3, height: 3, borderRadius: 2, background: 'var(--border-mid)' }} />
          <a
            href={HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'inherit',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon icon={ExternalLink} size={12} color="var(--text-hint)" />
            Help
          </a>
        </div>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 12, alignItems: 'baseline' }}>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}
